import { useState, useEffect, useRef } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  Shield, Heart, Car, Umbrella, Users, Share2, LogOut,
  ChevronRight, Zap, Plus, Clock, CheckCircle, AlertTriangle,
  ArrowRight, KeyRound, Upload, X, Loader2
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const CAT = {
  Health: { color: "#22c55e", bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-600", icon: Heart, label: "Health" },
  Life: { color: "#f59e0b", bg: "bg-[#D4A017]/10", border: "border-[#D4A017]/30", text: "text-[#A87608]", icon: Umbrella, label: "Life" },
  Vehicle: { color: "#3b82f6", bg: "bg-[#EDF1F8]/30", border: "border-blue-500/30", text: "text-[#2E3B57]", icon: Car, label: "Vehicle" },
  PA: { color: "#f43f5e", bg: "bg-rose-500/10", border: "border-rose-500/30", text: "text-rose-600", icon: Zap, label: "PA" }
};

function CoverRing({ value, max, color, size = 56 }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const data = [{ value: pct }, { value: 100 - pct }];
  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={size * 0.35} outerRadius={size * 0.48}
            startAngle={90} endAngle={-270} dataKey="value" stroke="none">
            <Cell fill={color} />
            <Cell fill="#1e293b" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [pinForm, setPinForm] = useState({ current: "", new1: "", new2: "" });
  const [pinError, setPinError] = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    const token = localStorage.getItem("client_token");
    if (!token) { navigate("/client/login"); return; }
    try {
      const res = await fetch(`${API}/api/client/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { localStorage.removeItem("client_token"); navigate("/client/login"); return; }
      setDashboard(await res.json());
    } catch { setError("Unable to load."); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { localStorage.removeItem("client_token"); localStorage.removeItem("client_info"); navigate("/client/login"); };

  const handleShare = () => {
    const ci = JSON.parse(localStorage.getItem("client_info") || "{}");
    const url = `${window.location.origin}/client/login?client=${ci.id || ""}`;
    if (navigator.share) navigator.share({ title: "My Insurance Dashboard", url });
    else { navigator.clipboard.writeText(url); setUploadMsg("Link copied!"); setTimeout(() => setUploadMsg(""), 2000); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { setUploadMsg("Only PDF files"); setTimeout(() => setUploadMsg(""), 3000); return; }
    setUploading(true); setUploadMsg("");
    try {
      const token = localStorage.getItem("client_token");
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/api/client/upload-policy`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (res.ok && data.success) { setUploadMsg("Policy uploaded!"); fetchDashboard(); } else setUploadMsg(data.detail || "Upload failed.");
      setTimeout(() => setUploadMsg(""), 3000);
    } catch { setUploadMsg("Upload failed."); setTimeout(() => setUploadMsg(""), 3000); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleChangePin = async () => {
    setPinError("");
    if (!pinForm.current || pinForm.current.length !== 4) { setPinError("Enter current 4-digit PIN"); return; }
    if (!pinForm.new1 || pinForm.new1.length !== 4 || !/^\d{4}$/.test(pinForm.new1)) { setPinError("New PIN must be 4 digits"); return; }
    if (pinForm.new1 !== pinForm.new2) { setPinError("PINs don't match"); return; }
    if (pinForm.current === pinForm.new1) { setPinError("Must be different from current"); return; }
    setPinSaving(true);
    try {
      const token = localStorage.getItem("client_token");
      const res = await fetch(`${API}/api/client/change-pin`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_pin: pinForm.current, new_pin: pinForm.new1 })
      });
      const data = await res.json();
      if (res.ok && data.success) { setShowChangePinModal(false); setPinForm({ current: "", new1: "", new2: "" }); setUploadMsg("PIN changed!"); setTimeout(() => setUploadMsg(""), 3000); }
      else setPinError(data.detail || "Failed");
    } catch { setPinError("Connection error."); }
    finally { setPinSaving(false); }
  };

  const filteredPolicies = dashboard?.policies?.filter(p => {
    if (!selectedMember) return true;
    return p.family_members?.includes(selectedMember) || p.insured_members_details?.some(m => m.name === selectedMember);
  }) || [];

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
      <Loader2 className="h-10 w-10 text-[#A87608] animate-spin" />
    </div>
  );

  if (error || !dashboard) return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center p-6 text-center">
      <div>
        <Shield className="h-12 w-12 text-red-600 mx-auto mb-3" />
        <p className="text-[#3A4557]">{error || "Something went wrong"}</p>
        <Button onClick={() => navigate("/client/login")} className="mt-4 bg-[#D4A017] text-[#0D1A3A] font-bold">Go Back</Button>
      </div>
    </div>
  );

  const { client, coverage, policies, family_members } = dashboard;
  const totalNum = coverage?.total_num || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#C9D4E6]">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 md:px-8 py-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#0D1A3A]" />
            </div>
            <div>
              <p className="text-[10px] text-[#3A4557]">Welcome back</p>
              <p className="text-sm font-bold text-[#1B2A4A]" data-testid="client-name">{client?.name || "User"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowChangePinModal(true)} className="h-8 w-8 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4] flex items-center justify-center transition-colors" data-testid="change-pin-btn" title="Change PIN">
              <KeyRound className="h-4 w-4 text-[#A87608]" />
            </button>
            <button onClick={handleShare} className="h-8 w-8 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4] flex items-center justify-center transition-colors" data-testid="share-btn">
              <Share2 className="h-4 w-4 text-[#3A4557]" />
            </button>
            <button onClick={handleLogout} className="h-8 w-8 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4] flex items-center justify-center transition-colors" data-testid="logout-btn">
              <LogOut className="h-4 w-4 text-[#3A4557]" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-12">
        {/* Hero Card */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
          className="mt-5 rounded-2xl bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/5 border border-[#D4A017]/20 p-5 md:p-8"
          data-testid="hero-coverage-card">
          <div className="md:flex md:items-center md:justify-between">
            <div>
              <p className="text-[#A87608]/60 text-[10px] font-semibold uppercase tracking-[0.2em]">Total Family Protection</p>
              <p className="text-[#1B2A4A] text-3xl md:text-4xl font-black mt-1" data-testid="total-coverage">{coverage?.total || "0"}</p>
              <p className="text-[#3A4557] text-xs mt-1">{policies?.length || 0} {policies?.length === 1 ? "policy" : "policies"} &bull; Premium: {coverage?.total_premium || "₹0"}/yr</p>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
              {coverage?.health_num > 0 && <span className="bg-green-500/15 border border-green-500/30 text-green-600 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Heart className="h-3 w-3" /> {coverage.health}</span>}
              {coverage?.life_num > 0 && <span className="bg-[#D4A017]/15 border border-[#D4A017]/30 text-[#A87608] text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Umbrella className="h-3 w-3" /> {coverage.life}</span>}
              {coverage?.vehicle_num > 0 && <span className="bg-blue-500/15 border border-blue-500/30 text-[#2E3B57] text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Car className="h-3 w-3" /> {coverage.vehicle}</span>}
              {coverage?.pa_num > 0 && <span className="bg-rose-500/15 border border-rose-500/30 text-rose-600 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Zap className="h-3 w-3" /> {coverage.pa}</span>}
            </div>
          </div>
        </motion.div>

        {/* Family Members */}
        {family_members?.length > 0 && (
          <div className="mt-6">
            <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Family Members</p>
            <Swipeable className="flex gap-2 pb-2">
              <button onClick={() => setSelectedMember(null)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${!selectedMember ? "bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/40" : "bg-[#EDF1F8]/50 text-[#3A4557] border border-[#C9D4E6]"}`}
                data-testid="filter-all-members">
                <Users className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" /> All
              </button>
              {family_members.map((fm, i) => (
                <button key={fm.name} onClick={() => setSelectedMember(selectedMember === fm.name ? null : fm.name)}
                  className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${selectedMember === fm.name ? "bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/40" : "bg-[#EDF1F8]/50 text-[#3A4557] border border-[#C9D4E6]"}`}
                  data-testid={`filter-member-${i}`}>
                  {fm.name.split(" ")[0]}
                  <span className="ml-1.5 text-[#3A4557]">({fm.policy_ids?.length || 0})</span>
                </button>
              ))}
            </Swipeable>
          </div>
        )}

        {/* Policies Grid */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest">Your Policies</p>
            <span className="text-[10px] text-[#3A4557]">{filteredPolicies.length} {filteredPolicies.length === 1 ? "policy" : "policies"}</span>
          </div>

          {filteredPolicies.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-[#3A4557] text-sm">No policies mapped yet</p>
              <p className="text-[#3A4557] text-xs mt-1">Upload a policy or contact your advisor</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredPolicies.map((policy, index) => {
                const cat = CAT[policy.insurance_category] || CAT.Health;
                const Icon = cat.icon;
                const isExpired = policy.policy_end_date && new Date(policy.policy_end_date) < new Date();
                const daysLeft = policy.policy_end_date ? Math.ceil((new Date(policy.policy_end_date) - new Date()) / 86400000) : null;
                const isExpiring = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;

                return (
                  <motion.div key={policy.id}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                    onClick={() => navigate(`/client/policy/${policy.id}`)}
                    className="bg-white/50 rounded-xl border border-[#C9D4E6] p-4 cursor-pointer hover:bg-[#EDF1F8]/50 hover:border-[#C9D4E6] transition-all group relative"
                    data-testid={`policy-card-${policy.id}`}>
                    
                    {isExpired && <span className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-600 border border-red-500/30">EXPIRED</span>}
                    {isExpiring && <span className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/30">RENEW SOON</span>}

                    <div className="flex gap-3">
                      <div className="relative flex-shrink-0">
                        <CoverRing value={policy.sum_insured_num} max={totalNum} color={cat.color} size={52} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Icon className={`h-5 w-5 ${cat.text}`} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${cat.bg} ${cat.text} ${cat.border} border`}>{cat.label}</span>
                        <p className="text-[11px] text-[#3A4557] mt-1">{policy.insurance_company}</p>
                        <p className="text-sm font-bold text-[#1B2A4A] truncate">{policy.plan_name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-[#A87608] font-semibold">{policy.sum_insured}</span>
                          <span className="text-[10px] text-[#3A4557]">₹{Number(String(policy.premium_amount || 0).replace(/,/g, '')).toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-[#C9D4E6]/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {daysLeft !== null && (
                          <span className={`text-[10px] flex items-center gap-1 ${isExpired ? 'text-red-600' : isExpiring ? 'text-[#A87608]' : 'text-[#3A4557]'}`}>
                            <Clock className="h-3 w-3" /> {isExpired ? "Expired" : `${daysLeft}d left`}
                          </span>
                        )}
                        {policy.family_members?.length > 0 && (
                          <span className="text-[10px] text-[#3A4557] flex items-center gap-1">
                            <Users className="h-3 w-3" /> {policy.family_members.length}
                          </span>
                        )}
                        {policy.riders?.length > 0 && (
                          <span className="text-[10px] text-[#3A4557]">{policy.riders.length} riders</span>
                        )}
                      </div>
                      <span className="text-[10px] text-[#A87608] font-semibold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                        Details <ChevronRight className="h-3 w-3" />
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upload Section */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-6">
          <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Upload Your Own Policy</p>
          <div onClick={() => !uploading && fileInputRef.current?.click()}
            className="bg-white/30 rounded-xl border-2 border-dashed border-[#C9D4E6] hover:border-[#D4A017]/40 p-6 md:p-8 transition-colors cursor-pointer flex flex-col items-center gap-2"
            data-testid="upload-policy-area">
            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleUpload} data-testid="upload-file-input" />
            {uploading ? (
              <>
                <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
                <p className="text-sm text-[#A87608] font-medium">Analyzing with AI...</p>
              </>
            ) : (
              <>
                <div className="h-12 w-12 rounded-xl bg-[#D4A017]/10 border border-[#D4A017]/30 flex items-center justify-center">
                  <Upload className="h-6 w-6 text-[#A87608]" />
                </div>
                <p className="text-sm text-[#2E3B57] font-medium">Tap to upload insurance PDF</p>
                <p className="text-xs text-[#3A4557]">AI will extract all policy details automatically</p>
              </>
            )}
          </div>
          {uploadMsg && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className={`text-xs font-medium text-center mt-2 ${uploadMsg.includes("success") || uploadMsg.includes("copied") || uploadMsg.includes("changed") || uploadMsg.includes("uploaded") ? "text-green-600" : "text-red-600"}`}>
              {uploadMsg}
            </motion.p>
          )}
        </motion.div>

        {/* Coverage Breakdown (Desktop: side by side, Mobile: grid) */}
        {policies?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="mt-6" data-testid="coverage-breakdown">
            <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Coverage Breakdown</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Health", value: coverage?.health, num: coverage?.health_num, color: "#22c55e", icon: Heart },
                { label: "Life", value: coverage?.life, num: coverage?.life_num, color: "#f59e0b", icon: Umbrella },
                { label: "Vehicle", value: coverage?.vehicle, num: coverage?.vehicle_num, color: "#3b82f6", icon: Car },
                { label: "PA", value: coverage?.pa, num: coverage?.pa_num, color: "#f43f5e", icon: Zap }
              ].filter(i => i.num > 0).map(item => (
                <div key={item.label} className="bg-white/50 rounded-xl border border-[#C9D4E6] p-3 flex items-center gap-3">
                  <div className="relative">
                    <CoverRing value={item.num} max={totalNum} color={item.color} size={42} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <item.icon className="h-3.5 w-3.5" style={{ color: item.color }} />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#3A4557]">{item.label}</p>
                    <p className="text-sm font-bold text-[#1B2A4A]">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <div className="mt-10 text-center">
          <p className="text-[10px] text-slate-500">Managed by your Insurance Advisor</p>
        </div>
      </div>

      {/* Change PIN Modal */}
      <AnimatePresence>
        {showChangePinModal && (
          <>
            <motion.div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowChangePinModal(false)} />
            <motion.div
              className="fixed z-50 bg-white border border-[#C9D4E6] rounded-2xl shadow-2xl p-6 w-[90%] max-w-sm left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              data-testid="change-pin-modal">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-[#A87608]" />
                  <p className="font-bold text-[#1B2A4A] text-sm">Change PIN</p>
                </div>
                <button onClick={() => setShowChangePinModal(false)} className="h-7 w-7 rounded-lg bg-[#EDF1F8] flex items-center justify-center hover:bg-[#E2E9F4]">
                  <X className="h-4 w-4 text-[#3A4557]" />
                </button>
              </div>
              <div className="space-y-3">
                {[
                  { label: "Current PIN", key: "current", testid: "current-pin-input" },
                  { label: "New PIN", key: "new1", testid: "new-pin-input" },
                  { label: "Confirm New PIN", key: "new2", testid: "confirm-pin-input" }
                ].map(({ label, key, testid }) => (
                  <div key={key}>
                    <label className="text-[10px] text-[#3A4557] font-medium block mb-1">{label}</label>
                    <input type="password" inputMode="numeric" maxLength={4} value={pinForm[key]}
                      onChange={e => { const v = e.target.value.replace(/\D/g, ''); setPinForm(f => ({ ...f, [key]: v })); setPinError(""); }}
                      className="w-full h-10 px-4 rounded-lg bg-[#EDF1F8] border border-[#C9D4E6] text-center text-lg font-bold tracking-[0.5em] text-[#1B2A4A] focus:ring-2 focus:ring-[#D4A017]/30 focus:border-[#D4A017] outline-none"
                      placeholder="----" data-testid={testid} />
                  </div>
                ))}
                {pinError && <p className="text-red-600 text-xs text-center">{pinError}</p>}
                <Button onClick={handleChangePin} disabled={pinSaving}
                  className="w-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0D1A3A] font-bold hover:from-[#D4A017] hover:to-[#D9A82C]"
                  data-testid="save-pin-btn">
                  {pinSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                  {pinSaving ? "Saving..." : "Change PIN"}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
